/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  EditorConfig,
  LexicalCommand,
  LexicalNode,
  NodeKey,
  NodeSelection,
  RangeSelection,
  SerializedElementNode,
} from "lexical"

import { addClassNamesToElement } from "@lexical/utils"
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $setSelection,
  createCommand,
  ElementNode,
  Spread,
} from "lexical"
import { OptionsLink } from "../../types"

export type SerializedInternalLinkNode = Spread<
  {
    type: "internal-link"
    url: string
    tableName: string
    id: string
    target?: string
    relationship?: string
    version: 1
  },
  SerializedElementNode
>

export class InternalLinkNode extends ElementNode {
  __url: string
  __tableName: string
  __id: any
  __target?: string
  __relationship?: string

  static getType(): string {
    return "internal-link"
  }

  static clone(node: InternalLinkNode): InternalLinkNode {
    return new InternalLinkNode(
      node.__url,
      node.__tableName,
      node.__id,
      node.__target,
      node.__relationship,
      node.__key
    )
  }

  constructor(
    url: string,
    tableName: string,
    id: string,
    target?: string,
    relationship?: string,
    key?: NodeKey
  ) {
    super(key)
    this.__url = url
    this.__tableName = tableName
    this.__id = id
    this.__target = target
    this.__relationship = relationship
  }

  createDOM(config: EditorConfig): HTMLAnchorElement {
    const element = document.createElement("a")
    element.href = this.__url
    if (this.__tableName) {
      element.setAttribute("data-tableName", this.__tableName)
    }
    if (this.__id) {
      element.setAttribute("data-id", this.__id)
    }
    if (this.__target) {
      element.target = this.__target
    }
    if (this.__relationship) {
      element.rel = this.__relationship
    }
    addClassNamesToElement(element, config.theme.link)
    return element
  }

  updateDOM(
    prevNode: InternalLinkNode,
    anchor: HTMLAnchorElement,
    config: EditorConfig
  ): boolean {
    const url = this.__url
    const tableName = this.__tableName
    const id = this.__id
    const target = this.__target
    const relationship = this.__relationship

    if (url !== prevNode.__url) {
      anchor.href = url
    }

    if (tableName !== prevNode.__tableName) {
      if (tableName) {
        anchor.setAttribute("data-tableName", tableName)
      } else {
        anchor.removeAttribute("data-tableName")
      }
    }

    if (id !== prevNode.__id) {
      if (id) {
        anchor.setAttribute("data-id", id)
      } else {
        anchor.removeAttribute("data-id")
      }
    }

    if (target !== prevNode.__target) {
      if (target) {
        anchor.target = target
      } else {
        anchor.removeAttribute("target")
      }
    }

    if (relationship !== prevNode.__relationship) {
      if (relationship) {
        anchor.rel = relationship
      } else {
        anchor.removeAttribute("rel")
      }
    }
    return false
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: (node: Node) => ({
        conversion: convertAnchorElement,
        priority: 1,
      }),
    }
  }

  static importJSON(
    serializedNode: SerializedInternalLinkNode
  ): InternalLinkNode {
    const node = $createInternalLinkNode(
      serializedNode.url,
      serializedNode.tableName,
      serializedNode.id,
      serializedNode.target,
      serializedNode.relationship
    )
    node.setFormat(serializedNode.format)
    node.setIndent(serializedNode.indent)
    node.setDirection(serializedNode.direction)
    return node
  }

  exportJSON(): SerializedInternalLinkNode {
    return {
      ...super.exportJSON(),
      relationship: this.getRelationship(),
      target: this.getTarget(),
      type: "internal-link",
      url: this.getURL(),
      tableName: this.getTableName(),
      id: this.getID(),
      version: 1,
    }
  }

  getURL(): string {
    return this.getLatest().__url
  }

  setURL(url: string): void {
    const writable = this.getWritable()
    writable.__url = url
  }

  getTableName(): string {
    return this.getLatest().__tableName
  }

  setTableName(tableName: string): void {
    const writable = this.getWritable()
    writable.__tableName = tableName
  }

  getID(): string {
    return this.getLatest().__id
  }

  setID(id: string): void {
    const writable = this.getWritable()
    writable.__id = id
  }

  getTarget(): string | undefined {
    return this.getLatest().__target
  }

  setTarget(target: string | undefined): void {
    const writable = this.getWritable()
    writable.__target = target
  }

  getRelationship(): string | undefined {
    return this.getLatest().__relationship
  }

  setRelationship(relationship: string | undefined): void {
    const writable = this.getWritable()
    writable.__relationship = relationship
  }

  insertNewAfter(selection: RangeSelection): null | ElementNode {
    const element = this.getParentOrThrow().insertNewAfter(selection)
    if ($isElementNode(element)) {
      const linkNode = $createInternalLinkNode(
        this.__url,
        this.__tableName,
        this.__id,
        this.__target,
        this.__relationship
      )
      element.append(linkNode)
      return linkNode
    }
    return null
  }

  canInsertTextBefore(): false {
    return false
  }

  canInsertTextAfter(): false {
    return false
  }

  canBeEmpty(): false {
    return false
  }

  isInline(): true {
    return true
  }

  extractWithChild(
    child: LexicalNode,
    selection: RangeSelection | NodeSelection,
    destination: "clone" | "html"
  ): boolean {
    if (!$isRangeSelection(selection)) {
      return false
    }

    const anchorNode = selection.anchor.getNode()
    const focusNode = selection.focus.getNode()

    return (
      this.isParentOf(anchorNode) &&
      this.isParentOf(focusNode) &&
      selection.getTextContent().length > 0
    )
  }
}

function convertAnchorElement(domNode: Node): DOMConversionOutput {
  let node: InternalLinkNode | null = null
  if (domNode instanceof HTMLAnchorElement) {
    node = $createInternalLinkNode(
      domNode.getAttribute("href") || "",
      domNode.getAttribute("data-tableName") || "",
      domNode.getAttribute("data-id") || "",
      domNode.getAttribute("target") || undefined,
      domNode.getAttribute("rel") || undefined
    )
  }
  return { node }
}

export function $createInternalLinkNode(
  url: string,
  tableName: string,
  id: string,
  target?: string,
  relationship?: string
): InternalLinkNode {
  return new InternalLinkNode(url, tableName, id, target, relationship)
}

export function $isInternalLinkNode(
  node: LexicalNode | null | undefined
): node is InternalLinkNode {
  return node instanceof InternalLinkNode
}

export const TOGGLE_INTERNAL_LINK_COMMAND: LexicalCommand<
  | true
  | {
      link: OptionsLink
      target?: string
      relationship?: string
      item: any
    }
  | null
> = createCommand()

export function unsetInternalLink() {
  const selection = $getSelection()

  if (selection !== null) {
    $setSelection(selection)
  }

  const sel = $getSelection()

  if (sel !== null) {
    const nodes = sel.extract()

    // Remove LinkNodes
    nodes.forEach((node) => {
      const parent = node.getParent()

      if ($isInternalLinkNode(parent)) {
        const children = parent.getChildren()

        for (let i = 0; i < children.length; i++) {
          const elm = children[i]
          if (elm) parent.insertBefore(elm)
        }

        parent.remove()
      }
    })
  }
}

export function setInternalLink(
  url: string,
  tableName: string,
  id: any,
  target?: string,
  relationship?: string
): void {
  const selection = $getSelection()

  if (selection !== null) {
    $setSelection(selection)
  }

  const sel = $getSelection()

  if (sel !== null) {
    const nodes = sel.extract()

    // Add or merge LinkNodes
    if ($isRangeSelection(sel) && sel.isCollapsed()) {
      return
    }
    if (nodes.length === 1) {
      const firstNode = nodes[0]

      // if the first node is a LinkNode or if its
      // parent is a LinkNode, we update the URL, target and relationship.
      if ($isInternalLinkNode(firstNode)) {
        firstNode.setURL(url)
        firstNode.setTableName(tableName)
        firstNode.setID(id)
        firstNode.setTarget(target)
        firstNode.setRelationship(relationship)
        return
      } else {
        const parent = firstNode?.getParent()

        if ($isInternalLinkNode(parent)) {
          // set parent to be the current linkNode
          // so that other nodes in the same parent
          // aren't handled separately below.
          parent.setURL(url)
          parent.setTableName(tableName)
          parent.setID(id)
          parent.setTarget(target)
          parent.setRelationship(relationship)
          return
        }
      }
    }

    let prevParent: ElementNode | InternalLinkNode | null = null
    let linkNode: InternalLinkNode | null = null

    nodes.forEach((node) => {
      const parent = node.getParent()

      if (
        parent === linkNode ||
        parent === null ||
        ($isElementNode(node) && !node.isInline())
      ) {
        return
      }

      if ($isInternalLinkNode(parent)) {
        linkNode = parent
        parent.setURL(url)
        parent.setTableName(tableName)
        parent.setID(id)
        parent.setTarget(target)
        parent.setRelationship(relationship)
        return
      }

      if (!parent.is(prevParent)) {
        prevParent = parent
        linkNode = $createInternalLinkNode(
          url,
          tableName,
          id,
          target,
          relationship
        )

        if ($isInternalLinkNode(parent)) {
          if (node.getPreviousSibling() === null) {
            parent.insertBefore(linkNode)
          } else {
            parent.insertAfter(linkNode)
          }
        } else {
          node.insertBefore(linkNode)
        }
      }

      if ($isInternalLinkNode(node)) {
        if (node.is(linkNode)) {
          return
        }
        if (linkNode !== null) {
          const children = node.getChildren()

          for (let i = 0; i < children.length; i++) {
            const elm = children[i]
            if (elm) linkNode.append(elm)
          }
        }

        node.remove()
        return
      }

      if (linkNode !== null) {
        linkNode.append(node)
      }
    })
  }
}
